/**
 * MarkBoqEditorModal — In-place editor for a mark's BoQ material
 * requirements.
 *
 *   • Left column shows the structural material-code catalog (single
 *     source of truth = `mtx_material_units` via GET
 *     /swm/structural-material-codes).
 *   • Right column shows the mark's current BoQ rows.
 *   • Add a code from the catalog with one click, edit qty in-place,
 *     and Save persists via PUT /swm/mark-numbers/{mark}/material-requirements.
 */
import { useEffect, useMemo, useState, Fragment } from 'react';
import { toast } from 'sonner';
import { X, Save, Loader2, Plus, Trash2, Search, Package } from 'lucide-react';
import api from '@/lib/api';

export default function MarkBoqEditorModal({ projectId, mark, onClose, onSaved }) {
  const [catalog, setCatalog] = useState([]);
  const [rows, setRows] = useState([]);           // editable requirement rows
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState('');

  // Marks whose name contains a '/' (e.g. '/PNOD-009') can't be
  // addressed via the path variant — ingress collapses `//` and the
  // route lookup misses.  For those, hit the `by-name?name=…` variant.
  const hasSlash = String(mark.markNumber || '').includes('/');
  const markGetUrl = hasSlash
    ? `/v1/projects/${projectId}/swm/mark-numbers/by-name?name=${encodeURIComponent(mark.markNumber)}`
    : `/v1/projects/${projectId}/swm/mark-numbers/${encodeURIComponent(mark.markNumber)}`;
  const markPutUrl = hasSlash
    ? `/v1/projects/${projectId}/swm/mark-numbers/by-name/material-requirements?name=${encodeURIComponent(mark.markNumber)}`
    : `/v1/projects/${projectId}/swm/mark-numbers/${encodeURIComponent(mark.markNumber)}/material-requirements`;

  useEffect(() => {
    (async () => {
      const [catRes, markRes] = await Promise.allSettled([
        api.get(`/v1/projects/${projectId}/swm/structural-material-codes`),
        api.get(markGetUrl),
      ]);
      if (catRes.status === 'fulfilled') {
        setCatalog(catRes.value.data?.items || []);
      } else {
        toast.error('Catalog load failed: ' + (catRes.reason?.response?.data?.detail || catRes.reason?.message));
      }
      if (markRes.status === 'fulfilled') {
        setRows((markRes.value.data?.materialRequirements || []).map((r, i) => ({
          _id: `${r.mCode}-${i}`,
          mCode: r.mCode,
          sectionProfile: r.sectionProfile || '',
          uom: r.uom || 'EA',
          boqQty: Number(r.boqQty || 0),
          boqWeight: Number(r.boqWeight || 0),
          allocatedQty: Number(r.allocatedQty || 0),
        })));
      } else {
        const status = markRes.reason?.response?.status;
        if (status && status !== 404) {
          toast.error('Mark load failed: ' + (markRes.reason?.response?.data?.detail || markRes.reason?.message));
        }
        setRows([]);
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, mark.markNumber]);

  const usedCodes = useMemo(() => new Set(rows.map(r => r.mCode)), [rows]);
  const filteredCatalog = useMemo(() => catalog.filter(c =>
    !q || c.materialCode.toLowerCase().includes(q.toLowerCase())
      || (c.description || '').toLowerCase().includes(q.toLowerCase())
      || (c.bulkItemCode || '').toLowerCase().includes(q.toLowerCase())), [catalog, q]);

  // Group filtered catalog by bulkItemCode so long lists are digestible.
  const grouped = useMemo(() => {
    const g = new Map();
    filteredCatalog.forEach(c => {
      const key = c.bulkItemCode || '— Ungrouped —';
      if (!g.has(key)) g.set(key, []);
      g.get(key).push(c);
    });
    return Array.from(g.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredCatalog]);
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const toggleGroup = (k) => setCollapsedGroups(s => {
    const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n;
  });

  const addCode = (c) => {
    if (usedCodes.has(c.materialCode)) {
      toast.info(`${c.materialCode} already in BoQ`); return;
    }
    setRows(rs => [...rs, {
      _id: `${c.materialCode}-${Date.now()}`,
      mCode: c.materialCode,
      sectionProfile: c.description || '',
      uom: c.uom || 'EA',
      boqQty: 0,
      boqWeight: 0,
      allocatedQty: 0,
    }]);
  };
  const removeRow = (id) => setRows(rs => rs.filter(r => r._id !== id));
  const setField = (id, field, val) => setRows(rs => rs.map(r =>
    r._id === id ? { ...r, [field]: val } : r));

  const totalBoq = rows.reduce((s, r) => s + Number(r.boqQty || 0), 0);
  const totalWt  = rows.reduce((s, r) => s + Number(r.boqWeight || 0), 0);

  const save = async () => {
    const payload = {
      requirements: rows
        .filter(r => r.mCode && Number(r.boqQty) > 0)
        .map(r => ({
          mCode: r.mCode,
          sectionProfile: r.sectionProfile || null,
          uom: r.uom || 'EA',
          boqQty: Number(r.boqQty),
          boqWeight: Number(r.boqWeight || 0),
        })),
    };
    if (payload.requirements.length === 0) {
      toast.error('Add at least one material with qty > 0'); return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(markPutUrl, payload);
      toast.success(`Saved ${data.requirementsSaved} materials · Total BoQ ${data.totalBoqQty.toLocaleString()} · Alloc ${data.overallAllocatedPercent}%`);
      onSaved?.(data);
      onClose();
    } catch (e) {
      toast.error('Save failed: ' + (e?.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose} data-testid="mark-boq-modal">
      <div className="rounded-xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--card-bg, #fff)', border: '1px solid var(--border-color, rgba(15,23,42,.12))' }}>
        {/* Header */}
        <div className="px-4 py-3 flex items-center gap-3 border-b"
          style={{ background: 'linear-gradient(90deg, #143A6B 0%, #0EA5E9 100%)', color: '#fff' }}>
          <Package className="w-5 h-5" />
          <div className="flex-1">
            <div className="text-sm font-bold">Mark BoQ Editor · {mark.markNumber}</div>
            <div className="text-[10.5px] opacity-90">Define per-material BoQ for this mark. Codes are synced with Material Tracking inventory (single source of truth).</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded" data-testid="mark-boq-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="p-10 flex items-center justify-center gap-2 text-xs" style={{ color: '#64748B' }}>
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 min-h-0">
            {/* Left · Catalog picker */}
            <div className="flex flex-col overflow-hidden border-r"
              style={{ borderColor: 'rgba(15,23,42,.08)' }}>
              <div className="px-3 py-2 flex items-center gap-2 border-b"
                style={{ background: '#F8FAFC', borderColor: 'rgba(15,23,42,.08)' }}>
                <Search className="w-3 h-3" style={{ color: '#64748B' }} />
                <input placeholder="Search structural material codes…"
                  value={q} onChange={e => setQ(e.target.value)}
                  className="flex-1 text-[11px] px-2 py-1 rounded border"
                  style={{ borderColor: 'rgba(15,23,42,.12)' }}
                  data-testid="mark-boq-search" />
                <span className="text-[9.5px]" style={{ color: '#64748B' }}>
                  {filteredCatalog.length} / {catalog.length}
                </span>
              </div>
              <div className="flex-1 overflow-auto">
                <table className="w-full text-[10.5px]">
                  <thead style={{ background: '#EEF2FF', position: 'sticky', top: 0 }}>
                    <tr style={{ color: '#1E3A8A' }}>
                      <th className="p-1.5 text-left uppercase text-[9px]">Material Code</th>
                      <th className="p-1.5 text-right uppercase text-[9px]">On-hand</th>
                      <th className="p-1.5 text-left uppercase text-[9px]">UOM</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped.map(([groupKey, groupRows]) => {
                      const isCollapsed = collapsedGroups.has(groupKey);
                      const groupOnHand = groupRows.reduce((s, r) => s + Number(r.onHandQty || 0), 0);
                      return (
                        <Fragment key={groupKey}>
                          <tr onClick={() => toggleGroup(groupKey)}
                            style={{ background: '#F1F5F9', cursor: 'pointer',
                                      borderTop: '1px solid rgba(15,23,42,.12)' }}
                            data-testid={`catalog-group-${groupKey}`}>
                            <td className="p-1.5 font-bold uppercase text-[10px]" style={{ color: '#334155' }}>
                              {isCollapsed ? '▶' : '▼'} {groupKey} <span className="font-normal opacity-60">({groupRows.length})</span>
                            </td>
                            <td className="p-1.5 text-right font-mono text-[10px]" style={{ color: '#0F766E' }}>
                              {groupOnHand.toLocaleString()}
                            </td>
                            <td colSpan={2}></td>
                          </tr>
                          {!isCollapsed && groupRows.map(c => {
                            const used = usedCodes.has(c.materialCode);
                            return (
                              <tr key={c.materialCode}
                                style={{ borderTop: '1px solid rgba(15,23,42,.06)',
                                          background: used ? '#F0FDF4' : '#fff', opacity: used ? 0.7 : 1 }}
                                data-testid={`catalog-row-${c.materialCode}`}>
                                <td className="p-1.5 pl-4" style={{ maxWidth: 360 }}>
                                  <div className="font-mono font-semibold whitespace-normal break-words leading-tight" style={{ color: '#143A6B' }}>
                                    {c.materialCode}
                                  </div>
                                </td>
                                <td className="p-1.5 text-right font-mono" style={{ color: '#0F766E' }}>
                                  {Number(c.onHandQty || 0).toLocaleString()}
                                </td>
                                <td className="p-1.5 text-[10px]" style={{ color: '#64748B' }}>{c.uom}</td>
                                <td className="p-1.5 text-center">
                                  <button onClick={() => addCode(c)} disabled={used}
                                    className="p-1 rounded"
                                    style={{ background: used ? '#94A3B8' : '#22c55e', color: '#fff' }}
                                    title={used ? 'Already in BoQ' : 'Add to BoQ'}
                                    data-testid={`add-to-boq-${c.materialCode}`}>
                                    <Plus className="w-3 h-3" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </Fragment>
                      );
                    })}
                    {filteredCatalog.length === 0 && (
                      <tr><td colSpan={4} className="p-6 text-center text-[10.5px]" style={{ color: '#94A3B8' }}>
                        No structural material codes match the filter.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Right · Current BoQ */}
            <div className="flex flex-col overflow-hidden">
              <div className="px-3 py-2 flex items-center gap-2 border-b"
                style={{ background: '#FEF3C7', borderColor: 'rgba(15,23,42,.08)' }}>
                <span className="text-[11px] font-bold" style={{ color: '#92400E' }}>BoQ for {mark.markNumber}</span>
                <span className="ml-auto text-[10px]" style={{ color: '#78350F' }}>
                  {rows.length} materials · Total qty <b>{totalBoq.toLocaleString()}</b> · Weight <b>{totalWt.toLocaleString()}</b>
                </span>
              </div>
              <div className="flex-1 overflow-auto">
                {rows.length === 0 ? (
                  <div className="p-6 text-center text-[10.5px]" style={{ color: '#94A3B8' }}>
                    Pick materials from the catalog on the left to build the BoQ for this mark.
                  </div>
                ) : (
                  <table className="w-full text-[10.5px]">
                    <thead style={{ background: '#F1F5F9', position: 'sticky', top: 0 }}>
                      <tr style={{ color: '#334155' }}>
                        <th className="p-1.5 text-left uppercase text-[9px]">Material Code</th>
                        <th className="p-1.5 text-left uppercase text-[9px]">Section / Profile</th>
                        <th className="p-1.5 text-right uppercase text-[9px]">BoQ Qty</th>
                        <th className="p-1.5 text-right uppercase text-[9px]">BoQ Weight (kg)</th>
                        <th className="p-1.5 text-left uppercase text-[9px]">UOM</th>
                        <th className="p-1.5 text-right uppercase text-[9px]">Alloc.</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(r => (
                        <tr key={r._id} style={{ borderTop: '1px solid rgba(15,23,42,.06)' }}
                          data-testid={`boq-row-${r.mCode}`}>
                          <td className="p-1.5 font-mono font-semibold whitespace-normal break-words leading-tight"
                            style={{ color: '#143A6B', maxWidth: 260 }}>{r.mCode}</td>
                          <td className="p-1.5">
                            <input value={r.sectionProfile || ''}
                              onChange={e => setField(r._id, 'sectionProfile', e.target.value)}
                              placeholder="e.g. IPE 300"
                              className="w-full text-[10.5px] px-1 py-0.5 border rounded"
                              style={{ borderColor: 'rgba(15,23,42,.12)' }} />
                          </td>
                          <td className="p-1.5 text-right">
                            <input type="number" min={0} step={0.1} value={r.boqQty}
                              onChange={e => setField(r._id, 'boqQty', Number(e.target.value))}
                              className="w-20 text-right font-mono text-[10.5px] px-1 py-0.5 border rounded"
                              style={{ borderColor: 'rgba(15,23,42,.12)' }}
                              data-testid={`boq-qty-${r.mCode}`} />
                          </td>
                          <td className="p-1.5 text-right">
                            <input type="number" min={0} step={0.1} value={r.boqWeight}
                              onChange={e => setField(r._id, 'boqWeight', Number(e.target.value))}
                              className="w-20 text-right font-mono text-[10.5px] px-1 py-0.5 border rounded"
                              style={{ borderColor: 'rgba(15,23,42,.12)' }} />
                          </td>
                          <td className="p-1.5">
                            <select value={r.uom} onChange={e => setField(r._id, 'uom', e.target.value)}
                              className="text-[10.5px] px-1 py-0.5 border rounded"
                              style={{ borderColor: 'rgba(15,23,42,.12)' }}>
                              {['EA','NOS','kg','m','m2','m3','drum','set','L'].map(u => <option key={u} value={u}>{u}</option>)}
                            </select>
                          </td>
                          <td className="p-1.5 text-right font-mono" style={{
                            color: r.allocatedQty >= r.boqQty && r.boqQty > 0 ? '#22c55e' : '#F59206',
                          }}>{Number(r.allocatedQty || 0).toLocaleString()}</td>
                          <td className="p-1.5 text-center">
                            <button onClick={() => removeRow(r._id)}
                              className="p-1 rounded hover:bg-red-50"
                              title="Remove"
                              data-testid={`remove-boq-${r.mCode}`}>
                              <Trash2 className="w-3 h-3" style={{ color: '#EF4444' }} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-4 py-3 flex items-center gap-3 border-t"
          style={{ background: '#F8FAFC', borderColor: 'rgba(15,23,42,.08)' }}>
          <div className="text-[10.5px]" style={{ color: '#64748B' }}>
            Codes stay in sync with <b>Material Tracking</b> · edits preserve existing hard-allocation / issued qty.
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={onClose} disabled={saving}
              className="px-3 py-1.5 rounded font-semibold text-[11px] border"
              style={{ borderColor: 'rgba(15,23,42,.15)', color: '#64748B' }}
              data-testid="mark-boq-cancel">Cancel</button>
            <button onClick={save} disabled={saving || rows.length === 0}
              className="px-4 py-1.5 rounded font-bold text-[11px] text-white flex items-center gap-1.5 shadow"
              style={{ background: rows.length === 0 ? '#94A3B8' : '#143A6B' }}
              data-testid="mark-boq-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {saving ? 'Saving…' : 'Save BoQ'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
